import { BEIGE_400, BEIGE_600, BOX_SHADOW_XS, GRAY_700, MOBILE_VIEW_WIDTH_QUERY, WHITE } from 'constants/styling/theme'
import { Box, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery } from '@mui/material'
import { CSSProperties, Fragment, ReactNode } from 'react'
import { SortDirection } from 'constants/misc'

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import SwapVertIcon from '@mui/icons-material/SwapVert'

/**
 * Styled table row with hover background styling.
 */
const StyledTableRow = styled(TableRow)(() => ({
  '&.MuiTableRow-hover': {
    ':hover': {
      backgroundColor: BEIGE_400,
    }
  },
}))

/**
 * Column definition for {@link MUITable}.
 *
 * @typeParam DataType - Shape of the data object stored in each row.
 */
export interface MUITableColumn<DataType> {
  /** Unique column identifier used for sorting */
  name: unknown

  /** Translation key or label displayed in the table header */
  translation: string

  /** Column width for desktop view */
  size: CSSProperties['width']

  /** Column width for mobile view */
  mobileSize: CSSProperties['width']

  /** Whether the column supports sorting */
  isSortable: boolean

  /**
   * Renders a table cell for the given row item.
   *
   * @param data - Row data wrapper containing id and data object
   */
  renderCell: (data: MUITableDataItem<DataType>) => ReactNode
}

/**
 * Wrapper representing a single table row item.
 *
 * @typeParam DataType - Shape of the row data.
 */
export interface MUITableDataItem<DataType> {
  /** Unique row identifier */
  id: string | number

  /** Actual data associated with the row */
  data: DataType
}

/**
 * Props for the {@link MUITable} component.
 *
 * @typeParam DataType - Shape of the row data.
 * @typeParam Columns - Tuple of column definitions.
 */
interface Props<DataType, Columns extends readonly MUITableColumn<DataType>[]> {
  /** Column configuration */
  columns: Columns

  /** Data rows rendered in the table */
  data: MUITableDataItem<DataType>[]

  /** Currently sorted column */
  sortBy?: Columns[number]['name']

  /** Current sort direction */
  sortDirection?: SortDirection

  /** Callback triggered when a sortable header is clicked */
  onSortChange?: (sortBy: Columns[number]['name']) => void

  /** Callback triggered when a table row is clicked */
  onRowClick?: (item: MUITableDataItem<DataType>) => void
}

/**
 * Generic, responsive Material UI table with optional sorting support.
 *
 * @typeParam DataType - Shape of the row data.
 * @typeParam Columns - Tuple of column definitions.
 */
export function MUITable<DataType, const Columns extends readonly MUITableColumn<DataType>[]>({
  columns,
  data,
  sortBy,
  sortDirection,
  onSortChange,
  onRowClick,
}: Props<DataType, Columns>) {

  /** Determines whether the table is rendered in mobile layout */
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  /**
   * Returns the appropriate sort icon for a column.
   *
   * @param columnSortBy - Column identifier
   */
  const getSortIcon = (columnSortBy: Columns[number]['name']) => {
    if (sortBy === columnSortBy) {
      return sortDirection === SortDirection.ASCENDING
        ? <ArrowUpwardIcon fontSize="small" />
        : <ArrowDownwardIcon fontSize="small" />
    }

    return <SwapVertIcon fontSize="small" />
  }

  /**
   * Handles click on sortable table headers.
   *
   * @param columnSortBy - Column identifier
   */
  const handleSortClick = (columnSortBy: Columns[number]['name']) => {
    if (onSortChange) {
      onSortChange(columnSortBy)
    }
  }

  return (
    <Box width="100%" overflow="auto">
      <Table
        size="medium"
        sx={{
          borderRadius: '.8rem',
          backgroundColor: WHITE,
          border: `.1rem solid ${BEIGE_600}`,
          boxShadow: BOX_SHADOW_XS,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          width: '100%',
          overflow: 'auto'
        }}
      >

        <TableHead>
          <TableRow sx={{
            boxShadow: '0px 1px 4px 0px rgba(19, 24, 29, 0.06)',
            position: 'sticky',
            top: -1,
            zIndex: 10,
          }}>
            {Object.values(columns).map(({ name, isSortable, size, mobileSize, translation }) => (
              <TableCell
                key={name as string}
                sx={{
                  width: isMobileView ? mobileSize : size,
                  minWidth: isMobileView ? mobileSize : size,
                  maxWidth: isMobileView ? mobileSize : size,
                  '&:last-child': { textAlign: 'right' },
                  backgroundColor: BEIGE_400,
                  cursor: isSortable ? 'pointer' : 'default',
                  '&:hover': isSortable ? {
                    backgroundColor: BEIGE_600,
                  } : {},
                }}
                onClick={isSortable ? () => handleSortClick(name) : undefined}
              >
                <Box display="flex" alignItems="center" gap="0.4rem">

                  <Typography
                    variant="text-xs"
                    fontWeight={600}
                    color={GRAY_700}
                    sx={{
                      fontWeight: sortBy === name ? 700 : 600,
                    }}
                  >
                    {translation}
                  </Typography>
                  {isSortable && getSortIcon(name)}
                </Box>

              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((item) => (
            <StyledTableRow
              hover
              key={item.id}
              onClick={() => onRowClick?.(item)}
              sx={{
                cursor: 'pointer',
                ':hover .property-title': {
                  textDecorationLine: 'underline',
                }
              }}
            >
              {columns.map(col => (
                <Fragment key={col.name as string}>{col.renderCell(item)}</Fragment>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>

      </Table>
    </Box>
  )
}

/**
 * Helper utility to define strongly typed table columns.
 *
 * @typeParam DataType - Shape of the row data.
 */
export function defineColumns<DataType>() {
  return <const C extends readonly MUITableColumn<DataType>[]>(columns: C) => columns
}
